// Analytics/services/endpoints/laboratory.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_LABORATORY === 'true',
    endpoints: {
        'total-lab-tests': true,
        'tests-by-department': true,
        'tests-by-type': true,
        'turnaround-time': true,
        'monthly-tests': true,
        'patients': true,
        'location': true,
        'insurance': true,
        'type': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockTotalTestsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [35, 42, 38, 45, 40, 48, 52] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 300
    }
});

const getMockTestsByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [120, 80, 60, 40] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 300
    }
});

const getMockTestsByTypeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [150, 100, 50] }
        ],
        categories: ["Blood Test", "Urine Test", "Culture"],
        count: 300
    }
});

const getMockTurnaroundTimeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Turnaround Time (Hours)", data: [2.5, 3.0, 2.8, 3.2, 2.9, 3.1, 3.0] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 2.9
    }
});

const getMockMonthlyTestsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Monthly Tests", data: [35, 42, 38, 45, 40, 48, 52] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 300
    }
});

const getMockPatientsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Patients", data: [25, 30, 28, 32, 29, 35, 38] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 217
    }
});

const getMockLocationResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [150, 100, 50] }
        ],
        categories: ["Main Lab", "Emergency Lab", "Outpatient Lab"],
        count: 300
    }
});

const getMockInsuranceResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [120, 100, 80] }
        ],
        categories: ["Private Insurance", "Public Insurance", "Self-Pay"],
        count: 300
    }
});

const getMockTypeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Tests", data: [150, 100, 50] }
        ],
        categories: ["Blood Test", "Urine Test", "Culture"],
        count: 300
    }
});

// Mock registry
const MOCK_REGISTRY = {
    'total-lab-tests': getMockTotalTestsResponse,
    'tests-by-department': getMockTestsByDepartmentResponse,
    'tests-by-type': getMockTestsByTypeResponse,
    'turnaround-time': getMockTurnaroundTimeResponse,
    'monthly-tests': getMockMonthlyTestsResponse,
    'patients': getMockPatientsResponse,
    'location': getMockLocationResponse,
    'insurance': getMockInsuranceResponse,
    'type': getMockTypeResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/laboratory`;
    const endpointPath = endpoint || '';
    console.log('Building URL:', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeRequest = async (params) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, getAuthHeaders());
        if (response.status === 200) {
            console.log('Laboratory API Response:', endpointPath, response.data);
            return response.data;
        }
    } catch (error) {
        console.error('Error fetching data: lab', url, error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating laboratory endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate) => 
        makeRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        })
    );
};

export const labServices = {
    getTotalTests: createEndpoint('total-lab-tests'),
    getTestsByDepartment: createEndpoint('tests-by-department'),
    getTestsByType: createEndpoint('tests-by-type'),
    getAverageTurnaroundTime: createEndpoint('turnaround-time'),
    getMonthlyTests: createEndpoint('monthly-tests'),
    getPatients: createEndpoint('patients'),
    getLocation: createEndpoint('location'),
    getInsurance: createEndpoint('insurance'),
    getType: createEndpoint('type'),
};

export const LaboratoryEndpoints = {
    STATS: '/total-tests',
    BY_DEPARTMENT: '/tests-by-department',
    BY_TYPE: '/tests-by-type',
    AVG_TURNAROUND_TIME: '/average-turnaround-time',
    MONTHLY_TESTS: '/monthly-tests',
    PENDING_TESTS: '/pending-tests',
    TEST_ACCURACY_RATE: '/test-accuracy-rate'
};
